import logo from '@/assets/hiredcoach-logo.svg';
import { cn } from '@/lib/utils';
import { User as UserIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';

type AvatarMode = 'none' | 'preset' | 'upload';
const STATIC_BASE = import.meta.env.BASE_URL || '/';

const staticAsset = (path: string): string => {
  const trimmedBase = STATIC_BASE.endsWith('/') ? STATIC_BASE.slice(0, -1) : STATIC_BASE;
  const trimmedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${trimmedBase}/${trimmedPath}`.replace('//', '/');
};

export type UserAvatarData = {
  firstName?: string;
  lastName?: string;
  avatar_mode?: AvatarMode;
  avatar_preset?: string | null;
  avatar_url?: string | null;
};

type UserAvatarProps = {
  user?: UserAvatarData | null;
  className?: string;
  fallback?: 'silhouette' | 'logo';
  alt?: string;
};

const getAvatarSrc = (
  user?: UserAvatarData | null,
  fallback: 'silhouette' | 'logo' = 'silhouette',
): string => {
  if (!user) {
    return fallback === 'logo' ? logo : staticAsset('/assets/avatars/default-silhouette.svg');
  }

  if (user.avatar_mode === 'upload' && user.avatar_url) {
    try {
      if (user.avatar_url.startsWith('http://') || user.avatar_url.startsWith('https://')) {
        const parsed = new URL(user.avatar_url);
        // Local-store links should come from the current app origin.
        if (parsed.pathname.includes('/api/storage/artifacts/')) {
          return `${window.location.origin}${parsed.pathname}${parsed.search}`;
        }
      }
    } catch {
      // Fall back to raw URL below.
    }
    return user.avatar_url;
  }

  if (user.avatar_mode === 'preset' && user.avatar_preset) {
    return staticAsset(`/assets/avatars/presets/${user.avatar_preset}.svg`);
  }

  return fallback === 'logo' ? logo : staticAsset('/assets/avatars/default-silhouette.svg');
};

export const getPresetAvatarSrc = (presetId: string): string =>
  staticAsset(`/assets/avatars/presets/${presetId}.svg`);

export const userHasCustomAvatar = (user?: UserAvatarData | null): boolean => {
  if (!user) return false;
  if (user.avatar_mode === 'upload' && !!user.avatar_url) return true;
  return user.avatar_mode === 'preset' && !!user.avatar_preset;
};

const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  className,
  fallback = 'silhouette',
  alt,
}) => {
  const src = getAvatarSrc(user, fallback);
  const label = alt || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User avatar';
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  return (
    <div
      className={cn(
        'relative flex shrink-0 overflow-hidden bg-transparent rounded-md',
        className,
      )}
    >
      {!imageFailed ? (
        <img
          src={src}
          alt={label}
          onError={() => setImageFailed(true)}
          className="h-full w-full object-contain"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted">
          {fallback === 'logo' ? (
            <img src={logo} alt="HiredCoach logo" className="h-full w-full object-contain" />
          ) : (
            <UserIcon className="h-5 w-5 text-gray-500" />
          )}
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
